// @flow

/**
 * Feedback Rating Component
 * Allows users to rate agent responses with thumbs up/down and optional comment
 * Integrates with Langfuse for feedback tracking
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { usePromptChatDispatch, useFeedbackService } from '../context';
import { useScrollService } from '../context/ScrollServiceContext';

const Container = styled.div`
  ${props => !props.$inline ? `
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  ` : `
    flex-grow: 1;
  `}
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeedbackButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${props => props.$active ? '#1a73e8' : 'rgba(0, 0, 0, 0.6)'};
  background-color: ${props => props.$active ? 'rgba(26, 115, 232, 0.08)' : 'transparent'};
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover:not(:disabled) {
    color: #1a73e8;
    background-color: rgba(26, 115, 232, 0.04);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;

const CommentToggle = styled.button`
  background: none;
  border: none;
  padding: 0.375rem 0.5rem;
  font-size: 0.75rem;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  font-style: italic;
  
  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CommentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
  width: 100%;
  min-width: 300px;
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 0.5rem;
  border: 1px solid rgba(16, 22, 26, 0.15);
  border-radius: var(--button-border-radius);
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  background-color: #ffffff;
  transition: border-color 0.1s ease;
  
  &:focus {
    outline: none;
    border-color: #137cbd;
    box-shadow: 0 0 0 1px #137cbd, 0 0 0 3px rgba(19, 124, 189, 0.3), inset 0 1px 1px rgba(16, 22, 26, 0.2);
  }

  &::placeholder {
    color: rgba(92, 112, 128, 0.6);
  }

  &:disabled {
    background-color: rgba(206, 217, 224, 0.5);
    cursor: not-allowed;
  }
`;

const CommentActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const CommentButton = styled.button`
  padding: 0.375rem 0.875rem;
  border: none;
  border-radius: var(--button-border-radius);
  font-size: 0.875rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  ${props => props.$primary ? `
    background-color: var(--primary);
    color: white;
    
    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
    }
  ` : `
    background-color: transparent;
    color: rgba(0, 0, 0, 0.6);
    
    &:hover:not(:disabled) {
      background-color: rgba(0, 0, 0, 0.04);
    }
  `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StatusText = styled.div`
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
`;

const ErrorText = styled.div`
  font-size: 0.8125rem;
  color: #d93025;
`;

type Props = {
  messageId: string,
  traceId?: string,
  initialFeedback?: {
    value: 1 | 0 | null,
    comment?: string,
  },
  inline?: boolean,
};

const FeedbackRating = ({ messageId, traceId, initialFeedback, inline }: Props) => {
  const dispatch = usePromptChatDispatch();
  const scrollService = useScrollService();
  const feedbackService = useFeedbackService();
  const commentSectionRef = useRef(null);
  const commentInputRef = useRef(null);
  const [value, setValue] = useState(initialFeedback?.value ?? null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(initialFeedback?.comment ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // If no feedback service is provided, don't render anything
  if (!feedbackService) {
    return null;
  }

  // Update local state if initialFeedback changes (e.g., from Redux rehydration)
  useEffect(() => {
    if (initialFeedback?.value != null) {
      setValue(initialFeedback.value);
    }
    if (initialFeedback?.comment) {
      setComment(initialFeedback.comment);
    }
  }, [initialFeedback]);

  // Scroll comment section into view and focus textarea when it opens
  useEffect(() => {
    if (!showComment || !commentSectionRef.current) return;
    
    // Use scroll service to request scroll with medium priority
    scrollService.requestScroll(commentSectionRef.current, {
      priority: 'medium',
      behavior: 'smooth',
      block: 'end',
    });
    
    // Focus textarea after scroll
    const focusTimeout = setTimeout(() => {
      if (commentInputRef.current) {
        const el = commentInputRef.current;
        el.focus();
        try {
          const len = el.value.length;
          el.setSelectionRange(len, len);
        } catch (e) {
          // Ignore selection errors
        }
      }
    }, 300);

    return () => {
      clearTimeout(focusTimeout);
    };
  }, [showComment, scrollService]);

  const handleThumbClick = useCallback(async (newValue: 1 | 0) => {
    // If clicking the same button, toggle it off
    const finalValue = value === newValue ? null : newValue;
    setValue(finalValue);
    setError(null);

    // If we have a value, submit immediately (can change later)
    if (finalValue !== null) {
      setIsSubmitting(true);
      try {
        await feedbackService.submit({
          traceId,
          value: finalValue,
          comment: comment.trim() || undefined,
        });

        // Update Redux state
        dispatch({
          type: 'SET_FEEDBACK',
          messageId,
          feedback: {
            value: finalValue,
            comment: comment.trim() || undefined,
            submittedAt: Date.now(),
          },
        });
      } catch (err) {
        setError((err && err.message) || 'Failed to submit feedback');
        // Revert on error
        setValue(value);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Cleared the rating - submit null to clear server-side
      setIsSubmitting(true);
      try {
        await feedbackService.submit({
          traceId,
          value: null,
          comment: undefined,
        });

        dispatch({
          type: 'SET_FEEDBACK',
          messageId,
          feedback: {
            value: null,
            comment: undefined,
            submittedAt: Date.now(),
          },
        });
      } catch (err) {
        setError((err && err.message) || 'Failed to clear feedback');
        setValue(value);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [value, comment, traceId, messageId, dispatch]);

  const handleCommentSubmit = useCallback(async () => {
    if (value === null) {
      setError('Please select thumbs up or down first');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await feedbackService.submit({
        traceId,
        value,
        comment: comment.trim() || undefined,
      });

      dispatch({
        type: 'SET_FEEDBACK',
        messageId,
        feedback: {
          value,
          comment: comment.trim() || undefined,
          submittedAt: Date.now(),
        },
      });

      setShowComment(false);
    } catch (err) {
      setError((err && err.message) || 'Failed to submit comment');
    } finally {
      setIsSubmitting(false);
    }
  }, [value, comment, traceId, messageId, dispatch]);

  const handleCommentCancel = useCallback(() => {
    setShowComment(false);
    setComment(initialFeedback?.comment ?? '');
    setError(null);
  }, [initialFeedback]);

  return (
    <Container $inline={inline}>
      <ButtonRow>
        <FeedbackButton
          $active={value === 1}
          onClick={() => handleThumbClick(1)}
          disabled={isSubmitting}
          title="Thumbs up"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
          </svg>
          Good
        </FeedbackButton>

        <FeedbackButton
          $active={value === 0}
          onClick={() => handleThumbClick(0)}
          disabled={isSubmitting}
          title="Thumbs down"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/>
          </svg>
          Bad
        </FeedbackButton>

        {!showComment && value !== null && (
          <CommentToggle
            onClick={() => setShowComment(true)}
            disabled={isSubmitting}
          >
            {(comment && comment.trim().length > 0) || (initialFeedback?.comment && initialFeedback.comment.trim().length > 0)
              ? 'Edit comment'
              : 'Add comment'}
          </CommentToggle>
        )}
      </ButtonRow>

      {showComment && (
        <CommentSection ref={commentSectionRef}>
          <CommentTextarea
            ref={commentInputRef}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more about your feedback... (optional)"
            disabled={isSubmitting}
          />
          <CommentActions>
            <CommentButton onClick={handleCommentCancel} disabled={isSubmitting}>
              Cancel
            </CommentButton>
            <CommentButton
              $primary
              onClick={handleCommentSubmit}
              disabled={isSubmitting || value === null || !comment.trim()}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </CommentButton>
          </CommentActions>
        </CommentSection>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

export default FeedbackRating;
